import { constants } from "@qgiv/core-js";

/**
 * @public
 * @function calcStoreTaxes
 * @param {number} storeItemTotal Current store items amount
 * @param {number} shippingTotal Current select shipping method cost or 0
 * @param {number} taxRate Indicates whether tax is to be added and at what percent
 * @returns {string} Returns calculated tax amount, rounded to two decimal places
 * @description Calculates the Store Taxes amount using product settings
 */
export const calcStoreTaxes = (storeItemTotal, shippingTotal, taxRate) => {
    const _taxRatePercentage = parseFloat(Number(taxRate));
    const totalAmountToTax = Number(storeItemTotal) + Number(shippingTotal);
    const percentageTax = (_taxRatePercentage / 100) * totalAmountToTax;
    const taxAmount = percentageTax ? percentageTax : 0;
    // prettier-ignore
    const roundedTaxAmount = Math.round(taxAmount * 100) / 100;

    return `${roundedTaxAmount}`;
};

/**
 * @public
 * @function createEntityReplaceForRegistration
 * @description function that checks for Team and Classification against event level settings
 *      to determine which should get credit for the donation. This is different to the function
 *      that is responsible for generating the entity replace logic on the receipt page due to the
 *      different data that is available in state.
 * -------------------------------------------------------------------------
 * NOTE: Optional Donation respecting Event Settings
 *
 * Display recipientFinePrintLabel or optionalGiftLabel:
 *    Not IsNfp and enableDonationToParticipant, otherwise follow rules below.
 * Display recipientFinePrintLabelForNonFundraisingParticipants or optionalGiftLabelForNonFundraisingParticipants:
 *    There are rules to which Entity to replace %Entity% tag with
 *      1. Team: Participant is on a Team and enableDonationToTeam is true
 *      2. Classification: enableDonationToEvent and enableClassificationForDonation are true && participant has a classification
 *      3. Event: enableDonationToEvent is true, participant does not have a classification
 *    Edge case:
 *        participant is non-fundraising
 *        participant is not on a team or enableDonationToTeam is disabled
 *        enableDonationToEvent is disabled
 *        BE treats this a donation to the event, entityType: 4 (Event)
 *  -------------------------------------------------------------------------
 * @param {Array} registrations Array of object for each registrant's data
 * @param {object} teams Array of object for each team's data
 * @param {object} classificationValue Classification object from registration values
 * @param {Array} classifications Array of objects for all available classifications
 * @param {boolean} enableDonationToTeam Event level setting for allowing donations to team
 * @param {boolean}  enableDonationToEvent Event level setting for allowing donations to event
 * @param {number}  enableClassificationForDonation Event level setting that allows donations to be attributed to a classification
 * @param {string} title Event title to be used if neither team nor classification is appropriate
 * @returns {string} Title to replace %Entity% for optionalGiftLabelForNonFundraisingParticipants
 *      and recipientFinePrintLabelForNonFundraisingParticipants whenever it is used during the
 *      registration process.
 */
export const createEntityReplaceForRegistration = (
    registrations,
    teams,
    classificationValue,
    classifications,
    enableDonationToTeam,
    enableDonationToEvent,
    enableClassificationForDonation,
    title,
) => {
    // Team
    let teamName = "";
    const { Team = "" } = registrations[0];
    if (Team && teams.map((team) => `${team.id}`).includes(Team)) {
        const currentTeam = teams.find((team) => `${team.id}` === Team) || {};
        const { title = "" } = currentTeam;
        teamName = title;
    }

    if (teamName.length > 0 && enableDonationToTeam) {
        return teamName;
    }
    // Event
    // Check for Classification first
    if (
        classificationValue &&
        classificationValue.Classification &&
        classificationValue.Classification.length > 0 &&
        enableDonationToEvent &&
        enableClassificationForDonation
    ) {
        const selectedClassification =
            classifications.find(
                (cla) =>
                    cla.id === Number(classificationValue.Classification[0]),
            ) || {};
        if (Object.keys(selectedClassification).length > 0) {
            const { title: classificationTitle } = selectedClassification;
            return classificationTitle;
        }
    }
    // If not a classification, return event title
    // We are not checking for enableDonationToEvent here because of Edge case mentioned above
    return title;
};

// -------------------------------------------------------------------------
// MARK: Store Helpers
// -------------------------------------------------------------------------
/**
 * @public
 * @function findProductFromProductId
 * @description function that takes the products object that is structured by Category
 *              then filters and find the specific product data by productId
 * @param {string} productId Product id to search for
 * @param {object} products Object containing all Products sorted by Category
 * @returns {object} Object contain one specific product's data
 */
export const findProductFromProductId = (productId, products) => {
    const productCategory = products.filter((productListByCategory) => {
        return (
            productListByCategory.products.length > 0 &&
            productListByCategory.products.find((thisProduct) => {
                return thisProduct.id === productId;
            })
        );
    });
    const productInfo = productCategory[0].products.find((thisProduct) => {
        return thisProduct.id === productId;
    });
    return productInfo;
};

/**
 * @public
 * @function getAttributesSelectedFromFieldDataId
 * @description function that takes the field data id and returns an object of unique product
 *      data based on the users attribute selections
 * @param {string} fieldDataId Id assigned to form once all attributes have been selected
 * @param {string} productId Product id to search for
 * @param {object} products Object containing all Products sorted by Category
 * @returns {object} Object contain one specific fieldData by Id for a product
 */
export const getAttributesSelectedFromFieldDataId = (
    fieldDataId,
    productId,
    products,
) => {
    const productData = findProductFromProductId(productId, products);
    const fieldDataObject = fieldDataId
        ? productData.fieldData[fieldDataId]
        : {};

    return {
        fieldDataObject,
    };
};

/**
 * @public
 * @function hasActiveProductsToBeDisplayed
 * @description Function to dig down into the products(byCategory) array to
 *      determine if there are any available to show
 * @param {Array|object} products Array of objects by product category
 * @returns {boolean} Boolean true if there are products to display
 */
export const hasActiveProductsToBeDisplayed = (products) => {
    if (products.length === 0) return false;
    const hasNoActiveProductsToPopulateStore =
        products.length === 1 && products[0].products.length === 0;

    const productsAvailableInRegistration = products
        .map((productsByCategory) => {
            const productsDisplayableInReg = productsByCategory.products.length;

            return productsDisplayableInReg;
        })
        .reduce((previousValue, currentValue) => previousValue + currentValue);

    const hasItemsAvailableInReg =
        products &&
        (hasNoActiveProductsToPopulateStore ||
            productsAvailableInRegistration === 0)
            ? false
            : true;
    return hasItemsAvailableInReg;
};

/**
 * @public
 * @function getIndexOfItemAlreadyInStoreCart
 * @description Duplicate Items are recognized in two ways.  If the product being added has attributes then
 *              making a selection for all of the attributes will produce a unique Field_Data_Id.
 *              We then search the store cart for that exact field data id. If a product does not have any
 *              attributes then we simply look for and compare the product id.
 *              If neither can be found then we know the item is not already in the cart,
 *              therefore -1 is returned.
 * @param {object} storeCart Object containing all items added to cart by user
 * @param {object} currentProductInfo Specific info for product currently viewing in product detail modal
 * @param {string} Field_Data_Id Value assigned to current product once attributes are selected
 * @param {number} Product_Id Product Id used to compare items in cart if no attributes
 * @returns {number} Returns index of item in cart or -1 if not found in cart
 */
export const getIndexOfItemAlreadyInStoreCart = (
    storeCart,
    currentProductInfo,
    Field_Data_Id,
    Product_Id,
) => {
    const { hasAttributes } = currentProductInfo;
    const hasItemsInCart = storeCart && storeCart.length > 0;
    const currentProductHasAttributes = hasAttributes && Field_Data_Id !== "";

    const existingItemIndex =
        hasItemsInCart && currentProductInfo && currentProductHasAttributes
            ? storeCart.findIndex((item) => {
                  return item.Field_Data_Id === Field_Data_Id;
              })
            : !hasAttributes && Product_Id
              ? storeCart.findIndex((item) => {
                    return item.Product_Id === Product_Id;
                })
              : -1;

    return existingItemIndex;
};

/**
 * @public
 * @function getExceedingTeamRegistrantLimits
 * @description takes in relevant data for determining if a team registrant limit has been reached
 * @param {object} [settings] data to be evaluated
 * @param {Array} [settings.teams] teams array that exists on redux state
 * @param {string} [settings.Team] ID of the team to be evaluated
 * @param {Array} [settings.registrations] registrations array that exists on redux state: registration
 * @param {number} [settings.currentRegistration] current registration index in registrations. redux state: registration
 * @param {boolean} [settings.preSelect] whether team has been joined already by registrant
 * @returns {boolean} whether or not the team registrant limit has been met
 */
export const getExceedingTeamRegistrantLimits = ({
    teams,
    Team,
    registrations,
    currentRegistration,
    preSelect,
}) => {
    // since created teams will have the id of 'new-team', need to convert
    // id of teams in redux to strings for eval
    const selectedTeam = teams.find((team) => String(team.id) === Team) || {};

    const existingRemainingSpots =
        selectedTeam && Object.keys(selectedTeam).length > 0
            ? selectedTeam.remainingSpots
            : // this could be a new team, which will have default values from classification
              -1;

    const participantInRegistrationsState =
        Object.keys(registrations[currentRegistration]).length > 0;

    const sameTeamMembersInRegistrations = registrations
        .map((registrant) => registrant.Team)
        .filter((teamId) => teamId === Team).length;

    const totalTeamMembersOnCurrentRegistration =
        participantInRegistrationsState || preSelect
            ? sameTeamMembersInRegistrations
            : sameTeamMembersInRegistrations + 1;

    if (
        existingRemainingSpots !== -1 &&
        totalTeamMembersOnCurrentRegistration >= existingRemainingSpots
    ) {
        return true;
    }
    return false;
};

/**
 * @public
 * @function getDisplayShippingInfo
 * @description takes in relevant data for determining if a the shipping info should be displayed
 * @param {Array} products different categories of products and the products they contain
 * @param {Array} storeCart products that have been added to the user's cart
 * @param {boolean} offerShipping whether or not shipping is offered to the user
 * @returns {boolean} whether or not the shipping info should be asked to select shipping info
 */
export const getDisplayShippingInfo = (products, storeCart, offerShipping) => {
    const { cms } = constants;
    const hasProducts = hasActiveProductsToBeDisplayed(products);
    const hasItemsInCart = storeCart && storeCart.length > 0;
    const displayShippingInfo =
        hasProducts && offerShipping && (cms || (!cms && hasItemsInCart))
            ? true
            : false;

    return displayShippingInfo;
};

/**
 * @public
 * @function getDisplayParticipantCounterNumber
 * @description determines if the number of the registrant should be displayed. This number is only
 *              relevant to the user when the add another button is present.
 * @param {Array} categories list of categories that are available to a registrant
 * @param {boolean} additionalRegistrants whether the event has additional participants enabled
 * @param {boolean} hasParentRegistration whether the user is completing a fundraising center registration
 * @returns {boolean} whether the participant counter number should be displayed
 */
export const getDisplayParticipantCounterNumber = (
    categories,
    additionalRegistrants,
    hasParentRegistration,
) => {
    const hasCategoryWithSubregistrantsEnabled = categories.some((category) => {
        return category.attachSubregistrants === true;
    });

    const displayParticipantCounterNumber =
        additionalRegistrants &&
        hasCategoryWithSubregistrantsEnabled &&
        !hasParentRegistration;

    return displayParticipantCounterNumber;
};

/**
 * @public
 * @function getDisplayAddAnotherButton
 * @description determines if the add another button should be displayed
 * @param {boolean} additionalRegistrants whether the event has additional participants enabled
 * @param {boolean} hasParentRegistration whether the user is completing a fundraising center registration
 * @param {boolean} addAdditionalParticipantDisabled whether the button has been disabled. Can be due to a
 *        number of category level settings, event level settings, or limits. This will always be true if
 *        none of the categories have sub-registrants enabled.
 * @returns {boolean} whether the add another button should be displayed
 */
export const getDisplayAddAnotherButton = (
    additionalRegistrants,
    hasParentRegistration,
    addAdditionalParticipantDisabled,
) => {
    const displayAddAnotherButton =
        additionalRegistrants &&
        !hasParentRegistration &&
        !addAdditionalParticipantDisabled;

    return displayAddAnotherButton;
};

/**
 * @public
 * @function getIsTeamAttachedToClassification
 * @param {object} settings Settings object
 * @param {Array} [settings.teams] Array of Teams Available to join
 * @param {string|number} [settings.teamId] Team stored in Redux for Registration
 * @param {string|number} [settings.classificationId] classification to check if attached
 * @returns {object} Object with all the Formik fields that need to be reset and flags to
 * determine when we should set them.
 */
export const getIsTeamAttachedToClassification = ({
    teams,
    teamId,
    classificationId,
}) => {
    // Early return if no classificationId or teamId. It should be 0 if limitTeamJoiningToClassification is false
    if (!classificationId || !teamId) return false;

    const selectedTeam = teams?.find((team) => {
        return team.id !== "new-team" && Number(team.id) === Number(teamId);
    }, {});

    const isTeamAttachedToClassification =
        Number(selectedTeam?.classification) === Number(classificationId);

    return isTeamAttachedToClassification;
};
